// Rock City Coffee — collection-grid hydration
// Fetches products from /collections/{handle}/products.json and renders cards client-side.
// Bypasses Shopify's eager variant-loading on `collection.products` so collection pages
// don't blow Shopify's per-request render budget on this catalog (70-variant coffee products).
// Visual output mirrors snippets/card-product.liquid so the existing Studio component CSS
// applies unchanged.

(function () {
  if (window.customElements && customElements.get('rcc-collection-grid')) return;

  const escapeHtml = (s) => {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  };

  const formatMoney = (cents) => {
    const fmt = (window.Shopify && window.Shopify.formatMoney);
    if (typeof fmt === 'function') {
      try { return fmt(cents); } catch (_) {}
    }
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Append a Shopify CDN width param to an image URL
  const withWidth = (src, w) => {
    if (!src) return '';
    try {
      const u = new URL(src, window.location.origin);
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (_) {
      return src;
    }
  };

  class CollectionGrid extends HTMLElement {
    constructor() {
      super();
      this.page = 1;
      this.fetchInFlight = false;
      this.exhausted = false;
    }

    connectedCallback() {
      this.handle = this.dataset.handle || '';
      this.perPage = parseInt(this.dataset.perPage, 10) || 16;
      this.imageRatio = this.dataset.imageRatio || 'adapt';
      this.showSecondary = this.dataset.showSecondaryImage === 'true';
      this.showVendor = this.dataset.showVendor === 'true';
      this.sectionId = this.dataset.sectionId || '';

      this.grid = this.querySelector('[data-grid]');
      this.empty = this.querySelector('[data-empty]');
      this.loadMoreBtn = this.querySelector('[data-load-more]');
      this.loadMoreWrap = this.querySelector('[data-load-more-wrapper]');

      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', () => this.loadNext());
      }

      this.fetchPage(1, { replace: true });
    }

    async fetchPage(page, { replace = false } = {}) {
      if (this.fetchInFlight || this.exhausted) return;
      this.fetchInFlight = true;
      if (this.loadMoreBtn) this.loadMoreBtn.disabled = true;

      const url = new URL(window.location.origin + '/collections/' + encodeURIComponent(this.handle) + '/products.json');
      url.searchParams.set('limit', String(this.perPage));
      url.searchParams.set('page', String(page));

      try {
        const res = await fetch(url.toString(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        const products = (data && data.products) || [];

        if (replace) {
          this.grid.innerHTML = '';
        }

        if (page === 1 && products.length === 0) {
          this.grid.hidden = true;
          if (this.empty) this.empty.hidden = false;
          if (this.loadMoreWrap) this.loadMoreWrap.hidden = true;
          this.exhausted = true;
          return;
        }

        const html = products.map((p) => this.renderCard(p)).join('');
        this.grid.insertAdjacentHTML('beforeend', html);

        this.page = page;

        if (products.length < this.perPage) {
          this.exhausted = true;
          if (this.loadMoreBtn) this.loadMoreBtn.hidden = true;
        } else {
          if (this.loadMoreBtn) {
            this.loadMoreBtn.hidden = false;
            this.loadMoreBtn.disabled = false;
          }
        }
      } catch (err) {
        console.error('[rcc-collection-grid] fetch failed', err);
        if (this.empty && replace) {
          this.grid.hidden = true;
          this.empty.hidden = false;
          this.empty.innerHTML = '<h2 class="title title--primary">Could not load products. <a href="javascript:location.reload()" class="underlined-link link">Try again</a></h2>';
        }
      } finally {
        this.fetchInFlight = false;
      }
    }

    loadNext() {
      this.fetchPage(this.page + 1);
    }

    // Mirrors snippets/card-product.liquid output for visual parity with stock Studio CSS.
    renderCard(p) {
      const url = '/products/' + encodeURIComponent(p.handle);
      const title = escapeHtml(p.title);
      const sectionId = escapeHtml(this.sectionId);
      const id = p.id;

      // Pricing — products.json prices are dollar strings; convert to cents for Shopify formatMoney.
      const variants = Array.isArray(p.variants) ? p.variants : [];
      const cents = (s) => {
        const f = parseFloat(s);
        return isFinite(f) ? Math.round(f * 100) : NaN;
      };
      const priceCents = variants.map((v) => cents(v.price)).filter(Number.isFinite);
      const compareCents = variants.map((v) => cents(v.compare_at_price)).filter(Number.isFinite);
      const minPrice = priceCents.length ? Math.min(...priceCents) : 0;
      const maxPrice = priceCents.length ? Math.max(...priceCents) : 0;
      const maxCompare = compareCents.length ? Math.max(...compareCents) : 0;
      const onSale = maxCompare > minPrice;
      const hasPriceRange = minPrice !== maxPrice;
      const available = variants.some((v) => v.available);

      // Featured image (+ optional secondary on hover)
      const images = Array.isArray(p.images) ? p.images : [];
      const featured = images[0] || null;
      const secondary = (this.showSecondary && images[1]) || null;

      // Aspect ratio (mirrors card-product.liquid lines 22-32)
      let ratio = 1;
      if (this.imageRatio === 'portrait' && featured) ratio = 0.8;
      else if (this.imageRatio === 'adapt' && featured && featured.width && featured.height) {
        ratio = featured.width / featured.height;
      }
      if (!ratio || !isFinite(ratio)) ratio = 1;
      const ratioPct = (1 / ratio * 100).toFixed(2);

      const featuredHTML = featured ? this.imageHTML(featured, p.title, false) : '';
      const secondaryHTML = secondary ? this.imageHTML(secondary, '', true) : '';

      const priceHTML = this.priceHTML(minPrice, maxPrice, maxCompare, hasPriceRange, onSale, available);

      const noMediaBadge = !available
        ? `<span id="NoMediaStandardBadge-${sectionId}-${id}" class="badge badge--bottom-left color-background-2">Sold out</span>`
        : (onSale ? `<span id="NoMediaStandardBadge-${sectionId}-${id}" class="badge badge--bottom-left color-background-2">On sale</span>` : '');
      const badge = !available
        ? `<span id="Badge-${sectionId}-${id}" class="badge badge--bottom-left color-background-2">Sold out</span>`
        : (onSale ? `<span id="Badge-${sectionId}-${id}" class="badge badge--bottom-left color-background-2">On sale</span>` : '');

      const vendorHTML = (this.showVendor && p.vendor)
        ? `<div class="caption-with-letter-spacing light">${escapeHtml(p.vendor)}</div>`
        : '';

      const mediaWrap = featured
        ? `<div class="card__media"><div class="media media--transparent media--hover-effect">${featuredHTML}${secondaryHTML}</div></div>`
        : '';

      // Match card-product.liquid for standard card style with featured media:
      //   outer .card: NO .ratio class (Studio only adds it for the no-media + card-style edge case)
      //   inner .card__inner: HAS .ratio (this is what actually does aspect-ratio sizing)
      // Adding .ratio to BOTH causes double aspect-ratio reservation = each card 2× too tall.
      return `
        <li class="grid__item">
          <div class="card-wrapper underline-links-hover">
            <div class="card card--standard ${featured ? 'card--media' : 'card--text'} card--extend-height" style="--ratio-percent: ${ratioPct}%;">
              <div class="card__inner color-background-1 gradient ratio" style="--ratio-percent: ${ratioPct}%;">
                ${mediaWrap}
                <div class="card__content">
                  <div class="card__information">
                    <h3 class="card__heading">
                      <a href="${url}" id="StandardCardNoMediaLink-${sectionId}-${id}" class="full-unstyled-link">${title}</a>
                    </h3>
                  </div>
                  <div class="card__badge bottom left">${noMediaBadge}</div>
                </div>
              </div>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading${featured ? ' h5' : ''}">
                    <a href="${url}" id="CardLink-${sectionId}-${id}" class="full-unstyled-link">${title}</a>
                  </h3>
                  <div class="card-information">
                    ${vendorHTML}
                    ${priceHTML}
                  </div>
                </div>
                <div class="card__badge bottom left">${badge}</div>
              </div>
            </div>
          </div>
        </li>
      `;
    }

    imageHTML(img, alt, isSecondary) {
      const widths = [165, 360, 533, 720, 940, 1066];
      const sw = img.width || 1066;
      const srcset = widths
        .filter((w) => w <= sw)
        .map((w) => `${withWidth(img.src, w)} ${w}w`)
        .join(', ');
      const sizes = '(min-width: 1200px) 270px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)';
      return `<img
        srcset="${srcset}"
        src="${withWidth(img.src, 533)}"
        sizes="${sizes}"
        alt="${escapeHtml(alt || '')}"
        class="motion-reduce"
        loading="lazy"
        width="${img.width || ''}"
        height="${img.height || ''}"
      >`;
    }

    priceHTML(minCents, maxCents, maxCompareCents, hasRange, onSale, available) {
      const fromPrefix = hasRange ? '<span class="price__from">From </span>' : '';
      if (onSale && available) {
        return `
          <div class="price price--on-sale">
            <dl>
              <dt class="visually-hidden">Sale price</dt>
              <dd class="price__sale">
                <span class="price-item price-item--sale">${fromPrefix}${formatMoney(minCents)}</span>
              </dd>
              <dt class="visually-hidden">Regular price</dt>
              <dd class="price__compare">
                <s class="price-item price-item--regular">${formatMoney(maxCompareCents)}</s>
              </dd>
            </dl>
          </div>
        `;
      }
      return `
        <div class="price">
          <dl>
            <dt class="visually-hidden">Regular price</dt>
            <dd class="price__regular">
              <span class="price-item price-item--regular">${fromPrefix}${formatMoney(minCents)}</span>
            </dd>
          </dl>
        </div>
      `;
    }
  }

  customElements.define('rcc-collection-grid', CollectionGrid);
})();
